import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { KitchenTicketState } from "src/utils/KitchenTicketStates";

// Slice del nuevo KDS basado en KitchenTickets. Coexiste con el viejo
// `displays` slice mientras dure la migración; cuando Fase 4 deprecación esté
// completa, el viejo se elimina.

const initialState = {
  active: [], // tickets en PENDING/PREPARING/READY + cancelados sin confirmar
  history: [], // últimos DELIVERED + cancelados confirmados (6h, máx 50)
  currentWorkingDisplay: null,
  isLoading: false,
  error: null,
};

// ── Movimientos locales (optimistas) ──────────────────────────────────
// Misma máquina de estados que KitchenTicketService (Next/PreviousState,
// ResyncTicketStateFromLines): el cocinero ve el cambio al momento y el
// servidor lo confirma después (o se recarga si falla).
const ALIVE_ORDER = [
  KitchenTicketState.PENDING,
  KitchenTicketState.PREPARING,
  KitchenTicketState.READY,
  KitchenTicketState.DELIVERED,
];
const DATE_BY_STATE = {
  [KitchenTicketState.PREPARING]: "preparingDate",
  [KitchenTicketState.READY]: "readyDate",
  [KitchenTicketState.DELIVERED]: "deliveredDate",
};

export function nextKitchenState(s, advance) {
  const i = ALIVE_ORDER.indexOf(s);
  if (i < 0) return s; // CANCELLED u otro: no se mueve
  const j = advance ? Math.min(i + 1, ALIVE_ORDER.length - 1) : Math.max(i - 1, 0);
  return ALIVE_ORDER[j];
}

function applyState(entity, target, nowIso) {
  entity.state = target;
  const field = DATE_BY_STATE[target];
  if (field && !entity[field]) entity[field] = nowIso;
}

function moveTicketToHistory(state, idx) {
  const [t] = state.active.splice(idx, 1);
  const hidx = state.history.findIndex((h) => h.id === t.id);
  if (hidx >= 0) state.history[hidx] = t;
  else state.history.unshift(t);
  if (state.history.length > 50) state.history.length = 50;
}

/** Aplica en el estado lo que hará el servidor con {ticketIds, lineIds}. */
export function applyMove(state, { ticketIds = [], lineIds = [], advance = true, now } = {}) {
  const nowIso = now || new Date().toISOString();
  const tIds = new Set(ticketIds);
  const lIds = new Set(lineIds);
  const delivered = [];
  state.active.forEach((t) => {
    if (tIds.has(t.id)) {
      const target = nextKitchenState(t.state, advance);
      if (target === t.state) return;
      applyState(t, target, nowIso);
      (t.lines || []).forEach((l) => {
        if (l.isActive !== false) applyState(l, target, nowIso);
      });
    } else {
      const lines = (t.lines || []).filter((l) => lIds.has(l.id) && l.isActive !== false);
      let touched = false;
      lines.forEach((l) => {
        const target = nextKitchenState(l.state, advance);
        if (target === l.state) return;
        applyState(l, target, nowIso);
        touched = true;
      });
      if (!touched) return;
      const alive = (t.lines || []).filter((l) => l.isActive !== false);
      if (alive.length > 0) {
        const min = alive.reduce(
          (acc, l) => (l.state < acc ? l.state : acc),
          KitchenTicketState.DELIVERED,
        );
        if (min !== t.state) applyState(t, min, nowIso);
      }
    }
    if (t.state === KitchenTicketState.DELIVERED) delivered.push(t.id);
  });
  // Entregado = sale de la columna activa (como upsertTicket con estado terminal).
  delivered.forEach((id) => {
    const idx = state.active.findIndex((t) => t.id === id);
    if (idx >= 0) moveTicketToHistory(state, idx);
  });
}

const slice = createSlice({
  name: "kitchenTickets",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getTicketsSuccess(state, action) {
      const { displayId, active, history } = action.payload;
      state.isLoading = false;
      state.error = null;
      state.currentWorkingDisplay = displayId;
      state.active = active || [];
      state.history = history || [];
    },

    // Optimistic patch para un ticket — el back devuelve el ticket actualizado
    // y reemplazamos la entrada. Si no estaba en active y ahora le toca, lo
    // añadimos; si estaba y debe salir (DELIVERED / confirmed cancel), lo
    // sacamos y opcionalmente lo mandamos a history.
    upsertTicket(state, action) {
      const updated = action.payload;
      if (!updated) return;

      const idx = state.active.findIndex((t) => t.id === updated.id);

      const isTerminal =
        updated.state === KitchenTicketState.DELIVERED ||
        (updated.state === KitchenTicketState.CANCELLED && updated.cancelConfirmed);

      if (isTerminal) {
        if (idx >= 0) state.active.splice(idx, 1);
        // Lo metemos primero en history (más reciente arriba)
        const hidx = state.history.findIndex((t) => t.id === updated.id);
        if (hidx >= 0) state.history[hidx] = updated;
        else state.history.unshift(updated);
        if (state.history.length > 50) state.history.length = 50;
        return;
      }

      if (idx >= 0) state.active[idx] = updated;
      else state.active.push(updated);
    },

    applyLocalMove(state, action) {
      applyMove(state, action.payload || {});
    },
    removeTicket(state, action) {
      const id = action.payload;
      state.active = state.active.filter((t) => t.id !== id);
    },

    cleanWorkingDisplay(state) {
      state.currentWorkingDisplay = null;
      state.active = [];
      state.history = [];
    },
  },
});

export default slice.reducer;

export const {
  startLoading,
  stopLoading,
  hasError,
  getTicketsSuccess,
  upsertTicket,
  applyLocalMove,
  removeTicket,
  cleanWorkingDisplay,
} = slice.actions;

// ------------------------- Thunks -------------------------

// Dedupe: SignalR (HubDisplayKitchen) + los thunks batch (advance/back/print)
// llegan casi juntos y disparaban un doble GET por cada accion. Guardamos la
// request en vuelo por displayId y ignoramos duplicados <300ms.
const _inFlightDisplays = new Set();
const _pendingDisplays = new Set();
let _lastFetchAt = 0;
// Una sola petición por pantalla a la vez; si piden otra mientras tanto (aviso
// SignalR, fin de un movimiento…) se repite UNA vez al terminar en vez de
// perderse (antes se descartaba y la pantalla quedaba con datos viejos).
// Solo la primera carga de una pantalla marca isLoading: los refrescos en
// segundo plano no bloquean los botones del cocinero.
export function getTicketsByDisplay(displayId) {
  return async (dispatch, getState) => {
    if (!displayId) return;
    if (_inFlightDisplays.has(displayId)) {
      _pendingDisplays.add(displayId);
      return;
    }
    const now = Date.now();
    if (now - _lastFetchAt < 300) {
      if (!_pendingDisplays.has(displayId)) {
        _pendingDisplays.add(displayId);
        setTimeout(() => {
          if (_pendingDisplays.delete(displayId)) dispatch(getTicketsByDisplay(displayId));
        }, 300 - (now - _lastFetchAt));
      }
      return;
    }
    _inFlightDisplays.add(displayId);
    _lastFetchAt = now;
    const firstLoad = getState?.()?.kitchenTickets?.currentWorkingDisplay !== displayId;
    if (firstLoad) dispatch(startLoading());
    try {
      const r = await axios.get(`KitchenTicket/Display/${displayId}`);
      dispatch(
        getTicketsSuccess({
          displayId,
          active: r.data?.active ?? [],
          history: r.data?.history ?? [],
        }),
      );
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    } finally {
      _inFlightDisplays.delete(displayId);
      if (_pendingDisplays.delete(displayId)) dispatch(getTicketsByDisplay(displayId));
    }
  };
}

export function __resetKitchenTicketsForTests() {
  _inFlightDisplays.clear();
  _pendingDisplays.clear();
  _lastFetchAt = 0;
  _moveChains.clear();
}

// Mueve el ticket entero (cabecera): backend mueve ticket + todas sus lines.
export function advanceTicket(ticketId, employeeId) {
  return async (dispatch) => {
    try {
      const r = await axios.put(`KitchenTicket/${ticketId}/Advance`, null, {
        params: employeeId ? { employeeId } : undefined,
      });
      dispatch(upsertTicket(r.data));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

export function backTicket(ticketId, employeeId) {
  return async (dispatch) => {
    try {
      const r = await axios.put(`KitchenTicket/${ticketId}/Back`, null, {
        params: employeeId ? { employeeId } : undefined,
      });
      dispatch(upsertTicket(r.data));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

// Mueve UNA line individual sin tocar el resto del ticket. Útil cuando el
// cocinero termina solo las patatas pero la hamburguesa sigue en plancha.
export function advanceLine(lineId, employeeId) {
  return async (dispatch) => {
    try {
      const r = await axios.put(`KitchenTicket/Line/${lineId}/Advance`, null, {
        params: employeeId ? { employeeId } : undefined,
      });
      dispatch(upsertTicket(r.data));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

export function backLine(lineId, employeeId) {
  return async (dispatch) => {
    try {
      const r = await axios.put(`KitchenTicket/Line/${lineId}/Back`, null, {
        params: employeeId ? { employeeId } : undefined,
      });
      dispatch(upsertTicket(r.data));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

// Confirma una cancelación pendiente. El back marca CancelConfirmed=true y
// el upsert detecta el estado terminal → saca de active y manda a history.
export function confirmCancel(ticketId, employeeId) {
  return async (dispatch) => {
    try {
      await axios.put(`KitchenTicket/${ticketId}/ConfirmCancel`, null, {
        params: employeeId ? { employeeId } : undefined,
      });
      dispatch(removeTicket(ticketId));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

// Movimientos en lote: optimistas (la pantalla cambia al pulsar) y en serie
// por pantalla (dos pulsaciones seguidas no se adelantan en el servidor).
// Tras confirmar, refresco en segundo plano; si falla, se recarga lo real.
const _moveChains = new Map();

function runMove({ ticketIds = [], lineIds = [], advance, send }) {
  return (dispatch, getState) => {
    if (ticketIds.length === 0 && lineIds.length === 0) return Promise.resolve();
    const displayId = getState().kitchenTickets.currentWorkingDisplay;
    dispatch(applyLocalMove({ ticketIds, lineIds, advance }));
    const key = displayId ?? "none";
    const prev = _moveChains.get(key) || Promise.resolve();
    const run = prev
      .catch(() => {})
      .then(() => send())
      .then(
        () => (displayId ? dispatch(getTicketsByDisplay(displayId)) : undefined),
        (e) => {
          console.error(e);
          dispatch(hasError(e?.message ?? "error"));
          return displayId ? dispatch(getTicketsByDisplay(displayId)) : undefined;
        },
      );
    _moveChains.set(key, run);
    run.finally(() => {
      if (_moveChains.get(key) === run) _moveChains.delete(key);
    });
    return run;
  };
}

// «En cocina todo» / «Atrás todo» de una mesa: tickets simples y lines de menú
// en UNA petición (Batch). Contra un back anterior sin Batch, las dos de antes.
export function moveMesa(ticketIds, lineIds, advance, employeeId) {
  return runMove({
    ticketIds,
    lineIds,
    advance,
    send: async () => {
      const dir = advance ? "Advance" : "Back";
      try {
        await axios.put(`KitchenTicket/Batch/${dir}`, { ticketIds, lineIds, employeeId });
      } catch (e) {
        const status = e?.response?.status ?? e?.status;
        if (status !== 404 && status !== 405) throw e;
        if (ticketIds.length) await axios.put(`KitchenTicket/${dir}`, { ticketIds, employeeId });
        if (lineIds.length) await axios.put(`KitchenTicket/Line/${dir}`, { lineIds, employeeId });
      }
    },
  });
}

export function advanceTickets(ticketIds, employeeId) {
  return runMove({
    ticketIds: ticketIds || [],
    advance: true,
    send: () => axios.put(`KitchenTicket/Advance`, { ticketIds, employeeId }),
  });
}

export function backTickets(ticketIds, employeeId) {
  return runMove({
    ticketIds: ticketIds || [],
    advance: false,
    send: () => axios.put(`KitchenTicket/Back`, { ticketIds, employeeId }),
  });
}

export function advanceLines(lineIds, employeeId) {
  return runMove({
    lineIds: lineIds || [],
    advance: true,
    send: () => axios.put(`KitchenTicket/Line/Advance`, { lineIds, employeeId }),
  });
}

export function backLines(lineIds, employeeId) {
  return runMove({
    lineIds: lineIds || [],
    advance: false,
    send: () => axios.put(`KitchenTicket/Line/Back`, { lineIds, employeeId }),
  });
}

export function printTickets({
  ticketIds = [],
  lineIds = [],
  employeeId = null,
  moveToDelivered = true,
  actuallyPrint = true,
}) {
  return async (dispatch, getState) => {
    const tids = Array.isArray(ticketIds) ? ticketIds : [];
    const lids = Array.isArray(lineIds) ? lineIds : [];
    if (tids.length === 0 && lids.length === 0) return;
    try {
      await axios.post(`KitchenTicket/Print`, {
        ticketIds: tids,
        lineIds: lids,
        employeeId,
        moveToDelivered,
        actuallyPrint,
      });
      // Refetch puntual del display activo para reflejar el cambio.
      const displayId = getState().kitchenTickets.currentWorkingDisplay;
      if (displayId) dispatch(getTicketsByDisplay(displayId));
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

export function splitTicket({ ticketId, quantity, employeeId = null }) {
  return async (dispatch, getState) => {
    try {
      const r = await axios.post(`KitchenTicket/${ticketId}/Split`, {
        ticketId,
        quantity,
        employeeId,
      });
      // El clon nuevo + el original modificado → refetch para mantener
      // coherencia simple (split no es frecuente).
      const displayId = getState().kitchenTickets.currentWorkingDisplay;
      if (displayId) dispatch(getTicketsByDisplay(displayId));
      return r.data;
    } catch (e) {
      console.error(e);
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}
